// The approval card: one change an investigation asked for, and its answer (#261, C4).
//
// Compact (#809, #795 decision 3): what changes, on which resource, how risky, how
// long is left, and the two answers. The scope, what AWS says, who may approve and
// what is kept sit behind "Details". The request is the `request_action` row's
// payload and the phases are the platform's `action_*` rows (`api/changes.ts`), so
// the card says nothing the log does not; codes a person cannot act on — the
// identity check's purpose, an access id, a raw timestamp — it does not draw (the
// owner's decision on #809).
//
// **Who may press is the server's answer**, `approval.may_answer`: the buttons
// are off for anyone off the holder side, and the card says why.

import { useEffect, useRef, useState } from "react";

import { utcTime } from "../api/automation";
import type { ChangeAnswer, ChangeRow } from "../api/changes";
import { messageOf } from "../api/http";
import type { StepUp } from "../api/policy";
import { runPagePath } from "../runId";
import { useDeadline, useNow } from "./decisionState";
import { Chip, Field, Notice } from "./chrome";
import { ChangeProgress, useChangeAudit } from "./ChangeProgress";
import { TicketLinks } from "./InvestigationLinks";
import {
  StepUpDialog,
  useStepUpAttempt,
  type BeginPasskey,
  type FactorsReading,
} from "./StepUp";

export type OnAnswerChange = (
  changeId: string,
  answer: ChangeAnswer,
  stepUp: StepUp,
  reason: string,
) => Promise<{ outcome: string } | void>;

const text = (value: unknown): string =>
  typeof value === "string" ? value : "";
const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** What AWS's schema said, from the eligibility phase — or that it was not asked. */
function awsSays(change: ChangeRow): string {
  const ruling = change.phases.find((phase) => phase.phase === "eligibility");
  if (ruling === undefined) {
    return "AWS was not asked. Review the proposal before answering.";
  }
  const reasons = strings(ruling.payload.reasons);
  const verdict =
    ruling.payload.eligible === true ? "Eligible." : "Not eligible.";
  return [verdict, ...reasons].join(" ");
}

/** One patch operation as a person reads it: the property, what it held when the
 * investigation read it (`null` when it read none), and what it becomes. */
interface PropertyChange {
  property: string;
  before: string | null;
  after: string;
}

const shown = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value ?? null);

/** The value the investigation read before this operation (#882): its entry in
 * `resource_change.before`, by position and by path, or `null` for none read. */
function beforeOf(reads: unknown[], index: number, path: unknown): string | null {
  const read = record(reads[index]);
  return read.path === path && read.read === true && "value" in read ? shown(read.value) : null;
}

/** The resource the patch writes, and each property it changes; `null` for a commands-only proposal. */
function resourceOf(request: Record<string, unknown>): {
  type: string;
  identifier: string;
  patch: PropertyChange[];
} | null {
  if (typeof request.resource_change !== "object" || request.resource_change === null) return null;
  const block = record(request.resource_change);
  const operations = Array.isArray(block.patch) ? block.patch.map(record) : [];
  const reads = Array.isArray(block.before) ? block.before : [];
  return {
    type: text(block.resource_type),
    identifier: text(block.identifier),
    patch: operations.map((op, index) => ({
      property: text(op.path).replace(/^\//, "") || "The resource",
      before: beforeOf(reads, index, op.path),
      after: op.op === "remove" ? "removed" : shown(op.value),
    })),
  };
}

/** A commands-only proposal's commands, to run by hand. */
function theCommands(request: Record<string, unknown>): string {
  return (
    strings(request.suggested_commands).join("\n") ||
    "The proposal carries no details of the change."
  );
}

/** How long is left, said as the minute it is in; the absolute time is under Details. */
export function timeLeft(deadline: string, now: number): string {
  const at = Date.parse(deadline);
  if (!Number.isFinite(at)) return "No deadline given";
  const minutes = Math.ceil((at - now) / 60_000);
  if (minutes <= 0) return "Expired";
  if (minutes < 60) return `Expires in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `Expires in ${hours} h${minutes % 60 === 0 ? "" : ` ${minutes % 60} min`}`;
  return `Expires in ${Math.floor(hours / 24)} days`;
}

const expiresAt = (deadline: string): string =>
  Number.isFinite(Date.parse(deadline)) ? `At ${utcTime(deadline)}` : "No deadline given";

/** Why a change whose account is not connected offers no Approve: the server's own refusal
 * (`store/account_status.py::NOT_APPROVED`, #1123), said before anyone presses anything. */
const NOT_CONNECTED =
  "This change's AWS account is no longer connected to Torque Assist, so the change cannot be " +
  "approved or applied. It can still be declined.";

/** The check's own answer button looks like the one on the card that opened it. */
const CONFIRM_CLASS: Record<ChangeAnswer, string> = {
  approve: "btn btn--primary",
  decline: "btn btn--danger",
};

/** The side that answers, by name. */
function authorityOf(change: ChangeRow): string {
  const { approval } = change;
  return approval.authority === "grant"
    ? `partner access held by ${approval.holder.name || "the partner"}`
    : "the workspace";
}

function whoMay(change: ChangeRow): string {
  const { approval } = change;
  if (approval.authority === "grant") {
    return (
      `${approval.holder.name || "The partner"} answers through ` +
      `${approval.workspace.name || "the workspace"}'s partner access to approve writes: ` +
      "its members whose role may approve changes. The workspace's own members do not."
    );
  }
  return (
    `${approval.holder.name || "The workspace"} keeps write approval: its members whose role ` +
    "may approve changes."
  );
}

export function ApprovalCard({
  change,
  onAnswer,
  factors,
  beginPasskey,
}: {
  change: ChangeRow;
  onAnswer: OnAnswerChange;
  /**
   * The factors this session holds (#636, #659). The caller reads them from
   * `GET /auth/mfa`; the dialog offers those and no others, and nothing
   * while they are unread (#739).
   */
  factors: FactorsReading;
  /** Mint the assertion challenge for this change's purpose, for the dialog's passkey. */
  beginPasskey?: BeginPasskey;
}) {
  // The answer pressed, whose identity check is open; none until one is pressed (#809).
  const [choice, setChoice] = useState<ChangeAnswer | null>(null);
  // Each attempt spends its proof (a passkey's is single-use): a failed one draws a fresh check.
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [approved, setApproved] = useState(false);
  const { request, approval } = change;
  const scope = record(request.scope);
  const resource = resourceOf(request);
  const expired = useDeadline(change.expires_at);
  const now = useNow();
  const off = !approval.may_answer || busy || expired || answered;
  // An approval on an account that is not connected is refused (#1123): no Approve is offered.
  const approvable = change.account_connected !== false;
  const reasonId = `change-${change.change_id}-reason`;
  const byHandId = `change-${change.change_id}-by-hand`;
  const notConnectedId = `change-${change.change_id}-not-connected`;

  // Cancelled, focus goes back to the answer that opened the check.
  const approveButton = useRef<HTMLButtonElement | null>(null);
  const declineButton = useRef<HTMLButtonElement | null>(null);
  const returnTo = useRef<ChangeAnswer | null>(null);
  useEffect(() => {
    if (choice !== null || returnTo.current === null) return;
    const back = returnTo.current === "approve" ? approveButton.current : declineButton.current;
    returnTo.current = null;
    back?.focus();
  });

  const open = (answer: ChangeAnswer) => {
    setError(null);
    setProof(null);
    setChoice(answer);
  };
  const cancel = () => {
    returnTo.current = choice;
    setProof(null);
    setChoice(null);
  };

  // No proof, or an empty password or code, is nothing to send: sending one only
  // spends an identity check against the lockout (#659).
  const answer = async (given: ChangeAnswer) => {
    if (proof === null || unproven) return;
    setBusy(true);
    setError(null);
    try {
      const result = await onAnswer(change.change_id, given, proof, given === "decline" ? reason : "");
      setApproved(result?.outcome === "approved");
      setAnswered(true);
      setChoice(null);
      setProof(null);
    } catch (failure) {
      setError(messageOf(failure));
      spent();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel decision-card" data-testid="approval-card">
      <div className="panel__head">
        <h2>{text(request.title) || "A write the investigation asked for"}</h2>
        {text(request.risk) === "" ? null : (
          <Chip>{text(request.risk)} risk</Chip>
        )}
      </div>
      <div className="panel__body stack stack--tight">
        <dl className="kv approval-facts">
          {resource === null ? null : (
            <>
              <dt>Change</dt>
              <dd>
                <ul className="patch-diff">
                  {resource.patch.map((op, index) => (
                    <li key={index}>
                      <code>{op.property}</code>:{" "}
                      {op.before === null ? "current value" : <code>{op.before}</code>} →{" "}
                      <code>{op.after}</code>
                    </li>
                  ))}
                </ul>
              </dd>
              <dt>Resource</dt>
              <dd>
                {resource.type} {resource.identifier} in account{" "}
                {text(scope.aws_account_id) || "not named on the proposal"}
              </dd>
            </>
          )}
          <dt>Investigation</dt>
          <dd>
            <a href={runPagePath(change.run_id)}>Open the investigation</a>
          </dd>
          <dt>Time left</dt>
          <dd>{timeLeft(change.expires_at, now)}</dd>
        </dl>
        <p>
          {resource === null
            ? "Covers this change only."
            : "Covers this change only. Properties the patch does not name are unchanged."}
        </p>
        {approvable ? null : (
          <Notice id={notConnectedId} tone="bad" standing>
            {NOT_CONNECTED}
          </Notice>
        )}
        {!approvable || change.platform_applies ? null : (
          <Notice id={byHandId}>
            If you approve, the platform will not apply this change: someone applies it by
            hand in the account.
          </Notice>
        )}
        {approval.may_answer ? null : (
          <Notice tone="bad">
            You cannot answer this change: {whoMay(change)}
          </Notice>
        )}
        {expired ? (
          <Notice tone="bad">
            This request has expired. The investigation continues without this change;
            refresh to read its current state.
          </Notice>
        ) : null}
        {approved ? <ApprovedChangeProgress changeId={change.change_id} /> : null}
        {busy ? <p role="status">Recording your answer…</p> : null}
        {error === null ? null : (
          <Notice tone="bad">
            {error} Refresh to read the current request before trying again.
          </Notice>
        )}
        <details className="approval-details">
          <summary>Details</summary>
          <dl className="kv">
            {text(request.rationale) === "" ? null : (
              <>
                <dt>Why</dt>
                <dd>{text(request.rationale)}</dd>
              </>
            )}
            <dt>Scope</dt>
            <dd>
              {text(scope.aws_account_id) || "Account not named on the proposal"}
              <div>
                Services: {strings(scope.services).join(", ") || "Unknown"}
              </div>
              <div>
                Writes expected: {scope.writes_expected === true
                  ? "yes"
                  : scope.writes_expected === false ? "no" : "Unknown"}
              </div>
            </dd>
            {(change.tickets ?? []).length === 0 ? null : (
              <>
                <dt>Ticket</dt>
                <dd>
                  <TicketLinks tickets={change.tickets ?? []} />
                </dd>
              </>
            )}
            <dt>What AWS says</dt>
            <dd>{awsSays(change)}</dd>
            {resource !== null ? null : (
              <>
                <dt>Commands</dt>
                <dd>
                  <pre className="code">
                    <code>{theCommands(request)}</code>
                  </pre>
                </dd>
              </>
            )}
            <dt>Who may approve</dt>
            <dd>{whoMay(change)}</dd>
            <dt>Kept as evidence</dt>
            <dd>
              Who pressed, the authority ({authorityOf(change)}), and the identity check bound
              to this change alone, on the investigation's log and in the audit log.
            </dd>
            <dt>Expires</dt>
            <dd>
              {expiresAt(change.expires_at)}. Unanswered by then, the investigation resumes
              with no action taken.
            </dd>
          </dl>
        </details>
        {choice === null ? (
          <div className="actions">
            {approvable ? (
              <button
                ref={approveButton}
                type="button"
                className="btn btn--primary"
                disabled={off}
                aria-describedby={change.platform_applies ? undefined : byHandId}
                onClick={() => open("approve")}
              >
                Approve
              </button>
            ) : null}
            <button
              ref={declineButton}
              type="button"
              className="btn btn--danger"
              disabled={off}
              aria-describedby={approvable ? undefined : notConnectedId}
              onClick={() => open("decline")}
            >
              Decline
            </button>
          </div>
        ) : (
          <StepUpDialog
            key={`${choice}-${attempt}`}
            role="group"
            autoFocus
            boundTo="this change alone"
            purpose={approval.step_up_purpose}
            changeId={change.change_id}
            factors={factors}
            disabled={off}
            beginPasskey={beginPasskey}
            onProofChange={setProof}
            onError={setError}
          >
            {choice === "decline" ? (
              <Field
                id={reasonId}
                label="A reason, if you decline (the investigation reads it)"
                type="text"
                disabled={off}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            ) : null}
            <div className="actions">
              <button
                type="button"
                className={CONFIRM_CLASS[choice]}
                disabled={off || unproven}
                onClick={() => void answer(choice)}
              >
                {choice === "approve" ? "Approve" : "Decline"}
              </button>
              <button type="button" className="btn btn--quiet" disabled={busy} onClick={cancel}>
                Cancel
              </button>
            </div>
          </StepUpDialog>
        )}
      </div>
    </section>
  );
}


/** The approval receipt keeps reading the change after it leaves the waiting list. */
export function ApprovedChangeProgress({ changeId }: { changeId: string }) {
  const { audit, error } = useChangeAudit(changeId);
  if (error !== null) return <Notice tone="bad">The change's progress is unavailable: {error}</Notice>;
  if (audit === null) return <p role="status">Reading the change's progress…</p>;
  return <><p>Change status: {audit.status}</p><ChangeProgress audit={audit} /></>;
}
